import { useState, useRef } from 'react'
import { Form, useFormikContext } from 'formik'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import Modal from 'components/Modal'
import Info from 'components/RevenueCenters/Form/Drawers/Menu/Info'
import MenuModel from 'components/RevenueCenters/Form/Drawers/Menu/MenuModel'
import Merchant from 'components/RevenueCenters/Form/Drawers/Menu/Merchant'
import Hours from 'components/RevenueCenters/Form/Drawers/Menu/Hours'
import MenuImage from 'components/RevenueCenters/Form/Drawers/Menu/MenuImage'
import { SpySidebar } from 'components/shared/SpySidebar'

import { getTransformedOrderAheadHours } from 'utils/revenueCenters/transformOrderAheadHours'
import {
  getHandleChangeOperatingHours,
  getHandleChangeOrderAheadHours,
} from 'utils/revenueCenters/handleChangeHours'
import { getTransformedOperatingHours } from 'utils/revenueCenters/transformOperatingHours'
import { getAllConfigurationModels } from 'store/ConfigurationModels/selectors'
import { isMobileMenu } from 'utils/revenueCenters'

const Menu = ({ isMenuImageLoading }) => {
  const {
    values,
    setFieldValue,
    validateForm,
    errors,
    initialValues,
  } = useFormikContext()
  const { state: locationState } = useLocation()

  const isNewMenu = locationState?.isNewMenu ?? false
  // when there is a new menu we have to set menuIndex to the last index of menus array, which was created in Form > ServiceTypes component
  const menuIndex = isNewMenu
    ? values.menus.length - 1
    : locationState?.menuIndex
  const currentMenu = values?.menus?.[menuIndex]

  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const [
    validationErrorOperatingHours,
    setValidationErrorOperatingHours,
  ] = useState([])
  const [
    validationErrorOrderAheadHours,
    setValidationErrorOrderAheadHours,
  ] = useState([])
  const [selectedMenuServiceType, setSelectedMenuServiceType] =
    useState(currentMenu?.serviceType)

  const itemSettingsRef = useRef()
  const history = useHistory()

  const allConfigurationModels = useSelector(
    getAllConfigurationModels
  )

  const modalTitle = isNewMenu ? 'Add Menu' : 'Edit Menu'

  const isActiveMobileMenu = isMobileMenu(selectedMenuServiceType)

  const isCurrentMenuError = !isEmpty(errors.menus?.[menuIndex])

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ]

  const currentOperatingHours =
    values?.menus?.[menuIndex]?.operatingHours
  const currentOrderAheadHours =
    values?.menus?.[menuIndex]?.orderAheadHours ?? {}

  const transformedCurrentOrderAheadHours =
    getTransformedOrderAheadHours(currentOrderAheadHours) ?? {}
  const transformedCurrentOperatingHours =
    getTransformedOperatingHours(currentOperatingHours)

  const settings = [
    {
      Component: Info,
      ref: sectionRefs[0],
      text: 'Info',
      url: '#info',
      menuIndex,
    },
    {
      Component: MenuModel,
      ref: sectionRefs[1],
      text: 'Menu Model',
      url: '#menu-model',
      setSelectedMenuServiceType,
      currentMenu,
      menuIndex,
      showValidationErrors,
      isNewMenu,
    },
    {
      Component: MenuImage,
      isMenuImageLoading,
      currentMenu,
      ref: sectionRefs[2],
      text: 'Menu Image',
      url: '#menu-image',
    },
    {
      Component: Merchant,
      ref: sectionRefs[3],
      text: 'Menu Shift4 Merchant',
      url: '#menu-shift4-merchant',
      selectedMenuServiceType,
      menuIndex,
      currentMenu,
      showValidationErrors,
    },
    {
      Component: Hours,
      ref: sectionRefs[4],
      text: 'Business Hours',
      url: '#business-hours',
      hours: transformedCurrentOperatingHours,
      title: 'Business Hours',
      setValidationErrorsHours: setValidationErrorOperatingHours,
      validationErrorsHours: validationErrorOperatingHours,
      handleChangeHours: (hours) =>
        getHandleChangeOperatingHours(
          hours,
          currentOperatingHours,
          values,
          currentMenu,
          setFieldValue,
          setShowValidationErrors
        ),
    },
    {
      Component: Hours,
      ref: sectionRefs[5],
      text: 'Order Ahead Hours',
      url: '#order-ahead-hours',
      hours: transformedCurrentOrderAheadHours,
      title: 'Order Ahead Hours',
      setValidationErrorsHours: setValidationErrorOrderAheadHours,
      validationErrorsHours: validationErrorOrderAheadHours,
      handleChangeHours: (hours) =>
        getHandleChangeOrderAheadHours(
          hours,
          currentOrderAheadHours,
          values,
          currentMenu,
          setFieldValue,
          setShowValidationErrors
        ),
    },
  ]

  const disableUpdateButton =
    showValidationErrors && isCurrentMenuError

  const onCloseModal = () => {
    // when user click on close button, during creating new menu, we have to remove last element from menus array in order to not save rejected creating of menu
    if (isNewMenu) {
      setFieldValue(
        setFieldValue('menus', values?.menus?.slice(0, -1))
      )
    }

    // when user click on close button, during editing existing serviceType, we have to set serviceType to initial value, but
    // if it is one of already created serviceTypes we do not change values
    if (!values?.menus?.[menuIndex]?.isNewMenu) {
      setFieldValue(
        `menus.${menuIndex}`,
        initialValues?.menus?.[menuIndex]
      )
    }

    history.push({
      state: {
        ...history.location.state,
        menuUuid: null,
        modalType: null,
      },
    })
  }

  const onClickSaveButton = () => {
    setShowValidationErrors(true)

    // when new menu is created, we have to add configrationModelName based on selected configurationModelId due to display it on revenue center drawer
    if (isNewMenu) {
      const newMenu = {
        ...currentMenu,
        configurationModelName: allConfigurationModels?.find(
          (configurationModel) =>
            configurationModel.uuid ===
            currentMenu.configurationModelId
        )?.name,
      }

      setFieldValue(`menus.${menuIndex}`, newMenu)
    }

    validateForm().then((error) => {
      if (isEmpty(error?.menus?.[menuIndex])) {
        setShowValidationErrors(false)

        // during the creation of a new menu, we need to add a new assignedItemsToMenus array
        // because the assignedItemsToMenus are releated with each menu
        if (isNewMenu) {
          setFieldValue('assignedItemsToMenus', [
            ...values.assignedItemsToMenus,
            { menuUuid: currentMenu.uuid, assignedItemsUuids: [] },
          ])
        }

        if (isActiveMobileMenu) {
          setFieldValue(`menus.${menuIndex}.merchantId`, null)
        }

        return history.push({
          state: {
            ...history.location.state,
            menuUuid: null,
            modalType: null,
          },
        })
      }
    })
  }

  return (
    <Modal title={modalTitle} onClose={onCloseModal}>
      <div className="modal-body">
        <Form>
          <div className="item-settings" ref={itemSettingsRef}>
            {settings.map(({ Component, ref, text, ...props }) => (
              <section
                key={text}
                className="item-settings__section"
                ref={ref}
                id={text}
              >
                <Component
                  {...props}
                  key={text}
                  title={text}
                  showValidationErrors={showValidationErrors}
                />
              </section>
            ))}
            <div className="btn-wrapper">
              <button
                className="btn -update"
                onClick={onClickSaveButton}
                disabled={disableUpdateButton}
                type="button"
                data-cy="save-button"
              >
                Save
              </button>
            </div>
          </div>
        </Form>
        <SpySidebar settings={settings} root={itemSettingsRef?.cur} />
      </div>
    </Modal>
  )
}

export default Menu
